import React, { useState } from 'react';
import { ClientHeader } from './ClientHeader';
import { UnifiedProfileTab } from './UnifiedProfileTab';
import { SourceComparisonTab } from './SourceComparisonTab';
import { DataSummaryTab } from './DataSummaryTab';
import './ConvergedClientView.css';

interface AssetAllocation {
  name: string;
  value: number;
  percentage: number;
  [key: string]: string | number;
}

interface Account {
  account_name: string;
  account_id: string;
  source?: string;
  account_type: string;
  market_value?: number;
}

interface Holding {
  symbol: string;
  security_name: string;
  quantity?: number;
  market_value: number;
}

interface ConflictField {
  field: string;
  field_label: string;
  source_value: string | number;
  new_value?: string | number;
  resolved_value: string | number;
  strategy: string;
  sourceAName?: string;
  sourceBName?: string;
}

interface TrustMetrics {
  totalFieldsCompared: number;
  matchingFields: number;
  resolvedFields: number;
  missingFields: number;
  lastUpdated: string;
}

interface FieldTrust {
  field: string;
  unifiedValue: string | number;
  status: 'Active' | 'Missing';
}

interface ConvergedClientViewProps {
  clientName: string;
  unifiedId: string;
  segment?: string;
  advisor?: string;
  status?: 'Active' | 'Inactive' | 'Restricted' | 'Closed';
  lastUpdated: string;
  totalMarketValue: number;
  unrealizedGainLoss?: number;
  holdingsCount: number;
  accountsCount: number;
  lastCalculated: string;
  assetAllocation: AssetAllocation[];
  accounts: Account[];
  holdings: Holding[];
  dataTrust: {
    trustPercentage: number;
    sources: string[];
  };
  conflicts: ConflictField[];
  trustMetrics: TrustMetrics;
  fieldTrust: FieldTrust[];
  attributeDefinitionsCount: number;
  sourceComparisonCount: number;
  portfolioAccounts: any[];
  clientIdentifierIds: string[];
}

export const ConvergedClientView: React.FC<ConvergedClientViewProps> = ({
  clientName,
  unifiedId,
  segment,
  advisor,
  status,
  lastUpdated,
  totalMarketValue,
  unrealizedGainLoss,
  holdingsCount,
  accountsCount,
  lastCalculated,
  assetAllocation,
  accounts,
  holdings,
  dataTrust,
  conflicts,
  trustMetrics,
  fieldTrust,
  attributeDefinitionsCount,
  sourceComparisonCount,
  portfolioAccounts,
  clientIdentifierIds
}) => {
  const [activeTab, setActiveTab] = useState<string>('profile');

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="converged-client-view min-h-screen bg-gray-50">
      <ClientHeader
        clientName={clientName}
        unifiedId={unifiedId}
        segment={segment}
        advisor={advisor}
        status={status}
        lastUpdated={lastUpdated}
      />

      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="flex gap-1">
            {[
              { id: 'profile', label: 'Unified Profile' },
              { id: 'conflicts', label: 'Source Comparison' },
              { id: 'data', label: 'Data Summary' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm relative ${
                  activeTab === tab.id
                    ? 'text-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto py-8">
        <div className="space-y-6">

          {activeTab === 'profile' && (
            <UnifiedProfileTab
              totalMarketValue={totalMarketValue}
              unrealizedGainLoss={unrealizedGainLoss}
              holdingsCount={holdingsCount}
              accountsCount={accountsCount}
              lastCalculated={lastCalculated}
              assetAllocation={assetAllocation}
              accounts={accounts}
              holdings={holdings}
              portfolioAccounts={portfolioAccounts}
              dataTrust={dataTrust}
              conflicts={conflicts}
              formatCurrency={formatCurrency}
              attributeDefinitionsCount={attributeDefinitionsCount}
              sourceComparisonCount={sourceComparisonCount}
            />
          )}

          {activeTab === 'conflicts' && (
            <SourceComparisonTab
              conflicts={conflicts}
              sources={dataTrust.sources}
              totalMarketValue={totalMarketValue}
              accountsCount={accountsCount}
              holdingsCount={holdingsCount}
              clientIdentifier={unifiedId}
              clientAccounts={accounts}
              portfolioAccounts={portfolioAccounts}
              clientIdentifierIds={clientIdentifierIds}
            />
          )}

          {activeTab === 'data' && (
            <DataSummaryTab metrics={trustMetrics} fieldTrust={fieldTrust} />
          )}
        </div>
      </div>
    </div>
  );
};
